import { ActionIcon, Box, Checkbox, FileButton, Group, Input, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconPhoto, IconX } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCanAccess } from '@mygpt/app';

import { useWidgetFormContext } from '@/components/Widget/Embed/form-context';
import { useWidgetForm } from '@/hooks/useWidgetForm';

import { SettingCard } from './SettingCard';

/** 标题栏：聊天窗标题文案 + 头部背景图（仅小部件：小窗 + 全屏，/bot 无标题栏） */
export const WidgetTitleBar = () => {
  const { t } = useTranslation();
  const { form } = useWidgetFormContext();
  const { onOverrideChangeForForm } = useWidgetForm();
  const canAccessWidgetCustomization = useCanAccess('enable_widget_customization');

  const [backgroundImageSrc, setBackgroundImageSrc] = useState<string>('');
  const backgroundImageResetRef = useRef<() => void>(null);

  // 处理背景图片上传
  const handleBackgroundImageUpload = (file: File | null) => {
    if (!file) return;

    if (file.size >= 2 * 1024 * 1024) {
      showNotification({
        message: t('embeddings.upload.max_file_size', { size: '2MB' }),
        color: 'red',
      });
      backgroundImageResetRef.current?.();
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      const src = this.result as string;
      setBackgroundImageSrc(src);

      // 保存到表单
      form.setFieldValue('chatbot.chatHeaderImage.url', src);
      form.setFieldValue('chatbot.chatHeaderImage.checked', true);

      // 触发自动保存
      onOverrideChangeForForm('')({ type: 'blur', isTrusted: true });
    };
  };

  // 删除背景图片
  const removeBackgroundImage = () => {
    setBackgroundImageSrc('');
    form.setFieldValue('chatbot.chatHeaderImage.url', '');
    form.setFieldValue('chatbot.chatHeaderImage.checked', false);
    backgroundImageResetRef.current?.();

    // 触发自动保存
    onOverrideChangeForForm('')({ type: 'blur', isTrusted: true });
  };

  // 初始化背景图片状态
  useEffect(() => {
    setBackgroundImageSrc(form.values.chatbot.chatHeaderImage?.url || '');
  }, [form.values.chatbot.chatHeaderImage?.url]);

  return (
    <SettingCard title={t('widget.setting.groups.card_title_bar')}>
      <Stack gap={20}>
        <Group justify="space-between">
          <Checkbox
            disabled={!canAccessWidgetCustomization}
            label={t('widget.setting.chatbot.chat_header_display_label')}
            checked={form.values.chatbot.chatHeaderDisplay.checked}
            {...form.getInputProps('chatbot.chatHeaderDisplay.checked')}
            onChange={onOverrideChangeForForm('chatbot.chatHeaderDisplay.checked')}
          />
          <section className="w-[200px]">
            <Input
              disabled={!form.values.chatbot.chatHeaderDisplay.checked}
              autoComplete="off"
              {...form.getInputProps('chatbot.chatHeaderDisplay.value')}
              onBlur={onOverrideChangeForForm('chatbot.chatHeaderDisplay.checked')}
            />
          </section>
        </Group>

        {/* 聊天背景图片设置 */}
        <Stack gap={10}>
          <Checkbox
            disabled={!canAccessWidgetCustomization}
            label={t('widget.setting.chatbot.chat_header_image_label')}
            checked={form.values.chatbot.chatHeaderImage.checked}
            {...form.getInputProps('chatbot.chatHeaderImage.checked')}
            onChange={onOverrideChangeForForm('chatbot.chatHeaderImage.checked')}
          />
          <Text size="sm" c="gray.6">
            {t('widget.setting.chatbot.chat_header_image_desc')}
          </Text>
          <section>
            {backgroundImageSrc || form.values.chatbot.chatHeaderImage.url ? (
              <Box
                className="relative w-[390px] h-[68px] border border-gray-300 rounded-md bg-cover bg-center"
                style={{
                  backgroundImage: `url(${backgroundImageSrc || form.values.chatbot.chatHeaderImage.url})`,
                }}
              >
                <ActionIcon
                  variant="filled"
                  color="red"
                  size="xs"
                  className="absolute -top-2 -right-2"
                  onClick={removeBackgroundImage}
                  disabled={!canAccessWidgetCustomization}
                >
                  <IconX size={12} />
                </ActionIcon>
              </Box>
            ) : (
              <FileButton
                resetRef={backgroundImageResetRef}
                onChange={handleBackgroundImageUpload}
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                disabled={!canAccessWidgetCustomization}
              >
                {(props) => (
                  <Box
                    {...props}
                    className="w-[390px] h-[68px] border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"
                  >
                    <IconPhoto size={24} className="text-gray-400 mb-1" />
                    <Text size="xs" c="gray.6" ta="center">
                      {t('widget.setting.chatbot.upload_background')}
                    </Text>
                  </Box>
                )}
              </FileButton>
            )}
          </section>
        </Stack>
      </Stack>
    </SettingCard>
  );
};
